import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { fetchTripById, ApiTrip } from "../../service/travelerMapTripService";
import { transformTrip, TransformedTrip } from "../utils/tripTransformer";
import { FlightStatusMapper } from "../utils/flightStatusMapper";
import { ArrowIcon, WarningIcon } from "../icons";

import { hasAccountFeature } from "../../site/userService";
import '../style/flight.less';

interface Passenger {
  name: string;
  group: string;
  tripId: string;
  pfhId: number;
  currentFlightHasIssue: boolean;
  futureFlightHasIssue: boolean;
  index?: number; // Add index for unique identification
}

interface FlightInfo {
  type: 'flight' | 'airport';
  flightNumber: string;
  dep: string;
  arr: string;
  passengers?: Passenger[];
  tripCount: number;
  isCurrentFlightIssue: boolean,
  isFutureFlightIssue: boolean,
  processedFlightHistoryId?: string | number; // For airport hover - identifies the flight passenger is waiting for
  recordLocator?: string; // Add record locator for airport type
}

interface FlightPopupProps {
  map: mapboxgl.Map;
  lngLat: mapboxgl.LngLatLike;
  info: FlightInfo;
  popupShouldBeOnLeft?: boolean;
  onClose: () => void;
}

const FlightPopup: React.FC<FlightPopupProps> = ({
  map,
  lngLat,
  info,
  popupShouldBeOnLeft = false,
  onClose,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedPassenger, setSelectedPassenger] = useState<Passenger | null>(null);
  const [transformedTrip, setTransformedTrip] = useState<TransformedTrip | null>(null);
  const [loading, setLoading] = useState(false);
  const flightCardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isPopupOnLeft, setIsPopupOnLeft] = useState<boolean>(false);

  // Helper function to check if user has Trip Assist access (hyperlink functionality)
  const hasTripAssistAccess = (): boolean => {
    const hasTripAssist = hasAccountFeature('TRIP_ASSIST');
    const hasTripAssistReports = hasAccountFeature('TRIP_ASSIST_REPORTS');
    const hasAccess = hasTripAssist || hasTripAssistReports;
    
    return hasAccess;
  };

  // --- Reset passenger selection when flight info changes ---
  useEffect(() => {
    setSelectedPassenger(null);
    setTransformedTrip(null);
    setLoading(false);
    setIsPopupOnLeft(false);
  }, [info.flightNumber, info.dep, info.arr, info.tripCount]);

  // --- Keep popup anchored to map position ---
  useEffect(() => {
    const updatePosition = () => {
      const projected = map.project(lngLat);
      setPosition({ x: projected.x, y: projected.y });
      // Update hover position using placePopup function
      const pos = placePopup(projected);
      setHoverPosition(pos);
    };
    updatePosition();

    map.on("move", updatePosition);
    map.on("zoom", updatePosition);
    map.on("resize", updatePosition);
    return () => {
      map.off("move", updatePosition);
      map.off("zoom", updatePosition);
      map.off("resize", updatePosition);
    };
  }, [map, lngLat, selectedPassenger]);

  // Previous developer's placePopup function for consistent positioning
    const placePopup = (point: any) => {
    const container = map.getContainer().getBoundingClientRect();
    let result: { x: number; y: number } = { x: point.x, y: point.y };
    let isOnLeft = false;
    
    // Constants for popup dimensions
    const HOVER_POPUP_WIDTH = 220; // Approximate width of hover popup
    const DETAIL_POPUP_WIDTH = 405; // Width of passenger detail popup
    const POPUP_OFFSET = 20; // Standard offset from point
    const POPUP_SPACING = 5; // Space between hover and detail popups
    
    // Calculate total width needed if both popups are shown
    const totalWidth = selectedPassenger 
      ? HOVER_POPUP_WIDTH + POPUP_SPACING + DETAIL_POPUP_WIDTH 
      : HOVER_POPUP_WIDTH;
    
    // Get the actual map container coordinates
    const mapContainerRect = map.getContainer().getBoundingClientRect();
    const pointScreenX = point.x + mapContainerRect.left;
    
    // Check available space on right side (from point to right edge of map)
    const availableRightSpace = mapContainerRect.right - pointScreenX;
    const availableLeftSpace = pointScreenX - mapContainerRect.left;
    
    // Use pre-calculated positioning to avoid flickering
    isOnLeft = popupShouldBeOnLeft;
    
    // Calculate hover popup position
    let left = isOnLeft ? point.x - (HOVER_POPUP_WIDTH + POPUP_OFFSET) : point.x + POPUP_OFFSET;
    let top = point.y;
    
    // Handle vertical positioning - use estimated popup height if no element exists yet
    const estimatedPopupHeight = 200; // Approximate height for positioning calculation
    
    // Try to get actual popup element for more accurate positioning
    const hoverElement = document.querySelector('.hoverContainer, .locationContainer') as HTMLElement;
    const actualPopupHeight = hoverElement ? hoverElement.getBoundingClientRect().height : estimatedPopupHeight;
    
    // Vertical positioning logic
    if (point.y > actualPopupHeight) {
      top = point.y - actualPopupHeight;
    } else if (point.y <= actualPopupHeight && (mapContainerRect.bottom - (point.y + mapContainerRect.top)) > actualPopupHeight) {
      top = point.y;
    } else {
      top = point.y - (actualPopupHeight - (mapContainerRect.bottom - (point.y + mapContainerRect.top)));
    }
    
    result = { x: left, y: top };
    
    // Update the left/right state for passenger popup positioning
    setIsPopupOnLeft(isOnLeft);
    return result;
  };



  // --- Close popup when clicking outside or pressing ESC ---
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        (!detailRef.current || !detailRef.current.contains(e.target as Node))
      ) {
        onClose();
      }
    };
    const handleEsc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  // --- Connection Lines Component ---
  const ConnectionLines: React.FC = () => {
    const [linePositions, setLinePositions] = useState<{top: number, height: number, lineType: 'solid' | 'dotted', left: number}[]>([]);

    useEffect(() => {
      if (!transformedTrip?.flights || transformedTrip.flights.length <= 1) {
        setLinePositions([]);
        return;
      }

        const calculateLines = () => {
          const positions: {top: number, height: number, lineType: 'solid' | 'dotted', left: number}[] = [];
          const flightCards = flightCardsRef.current;
          const containerElement = flightCards[0]?.parentElement;

          if (!containerElement) return;

          for (let i = 0; i < transformedTrip.flights.length - 1; i++) {
            const currentCard = flightCards[i];
            const nextCard = flightCards[i + 1];

            if (currentCard && nextCard) {
              // Find circle elements
              const currentCircle = currentCard.querySelector('div[style*="border-radius: 50%"]') as HTMLElement;
              const nextCircle = nextCard.querySelector('div[style*="border-radius: 50%"]') as HTMLElement;
              
              if (currentCircle && nextCircle) {
                const containerRect = containerElement.getBoundingClientRect();
                const currentRect = currentCircle.getBoundingClientRect();
                const nextRect = nextCircle.getBoundingClientRect();
                
                // Calculate circle centers and positions
                const currentCenterY = currentRect.top + currentRect.height / 2 - containerRect.top;
                const nextCenterY = nextRect.top + nextRect.height / 2 - containerRect.top;
                const centerX = currentRect.left + currentRect.width / 2 - containerRect.left;
                
                // Calculate edge-to-edge positions (circle radius is ~6.5px)
                const circleRadius = currentRect.height / 2;
                const startY = currentCenterY + circleRadius; // Bottom edge of current circle
                const endY = nextCenterY - circleRadius;      // Top edge of next circle
                
                const currentFlight = transformedTrip.flights[i];
                let lineType: 'solid' | 'dotted' = 'dotted';
                
                // Determine if this hop connection should be solid
                // A hop connection becomes solid when the hop it represents has been completed
                // This happens when the current flight index is greater than the connection index
                const currentFlightIndex = transformedTrip.flights.findIndex(flight => flight.isCurrentFlight);
                
                
                // If we found a current flight and this connection is before the current flight,
                // it means this hop has been completed, so show solid line
                if (currentFlightIndex !== -1 && i < currentFlightIndex) {
                  lineType = 'solid';
                }

                if (Math.abs(endY - startY) > 5) { // Only show if significant distance between edges
                  positions.push({
                    top: Math.min(startY, endY),
                    height: Math.abs(endY - startY),
                    left: centerX,
                    lineType
                  });
                }
              }
            }
          }
          
          setLinePositions(positions);
        };

        const timer = setTimeout(calculateLines, 100); // Small delay for DOM to settle
      return () => clearTimeout(timer);
    }, [transformedTrip]);

    if (linePositions.length === 0) return null;

    return (
      <>
        {linePositions.map((line, index) => (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: `${line.left - 0.5}px`, // Center the 1px line
              top: `${line.top}px`,
              width: '1px',
              height: `${line.height}px`,
              background: line.lineType === 'solid' 
                ? '#4B5F7A' // Same color as arrow lines
                : 'transparent',
              backgroundImage: line.lineType === 'dotted' 
                ? 'repeating-linear-gradient(to bottom, #4B5F7A 0px, #4B5F7A 8px, transparent 8px, transparent 10px)'
                : 'none',
              pointerEvents: 'none',
              zIndex: 1
            }}
          />
        ))}
      </>
    );
  };

  // --- Handle passenger click and detail fetch ---
  const handlePassengerClick = async (p: Passenger) => {
    setSelectedPassenger(p);
    setTransformedTrip(null);
    setLoading(true);
    
    try {
      const apiTripDetail: ApiTrip = await fetchTripById(p.tripId);
      let pfhid = p.pfhId;
      let passengerName = p.name;
      // Transform the API response using tripTransformer
      const transformed = transformTrip(apiTripDetail, pfhid, passengerName);
      setTransformedTrip(transformed);
    } catch (err) {
      setTransformedTrip(null);
    } finally {
      setLoading(false);
    }
  };

  // Helper to format connection time
  const formatConnectionTime = (mct: number | null | undefined): string => {
    if (mct === null || mct === undefined) return "";
    
    if (mct < 0) {
      // Negative connection time indicates a missed connection or connection issue
      const absoluteMinutes = Math.abs(mct);
      const hours = Math.floor(absoluteMinutes / 60);
      const minutes = absoluteMinutes % 60;
      return `Connection Issue -${hours}H ${minutes}MINS`;
    }
    
    if (mct === 0) return "Connection 0MINS";
    
    const hours = Math.floor(mct / 60);
    const minutes = mct % 60;
    return `Connection ${hours}H ${minutes}MINS`;
  };

  // Helper to format status message for display using the new mapping system
  const formatStatusMessage = (flight: any): string => {
    if (!flight.status) return "UNKNOWN";
    
    // If status was overridden by issues, don't try to extract time from original message
    if (flight.hasStatusOverride) {
      const mappedStatus = FlightStatusMapper.mapStatus(flight.status, flight.statusIndicator);
      return mappedStatus.label;
    }
    
    // If no override, try to extract time from original status message
    const statusToUse = flight.originalStatusMessage || flight.status;
    const mappedStatus = FlightStatusMapper.mapStatus(statusToUse, flight.statusIndicator);
    
    // Include time if available and the combined text won't be too long for the small status box
    // Status box is ~168px wide with 10px font, so we limit to about 18-20 characters total
    if (mappedStatus.time && mappedStatus.time.length <= 6) {
      const combined = `${mappedStatus.label} ${mappedStatus.time}`;
      if (combined.length <= 20) {
        return combined;
      }
    }
    
    return mappedStatus.label;
  };

  // Helper to check if current flight has hop issues
  const hasCurrentFlightHopIssues = (transformedTrip: TransformedTrip | null): boolean => {
    if (!transformedTrip) return false;    
    return false; 
  };

  // Helper to check if actual time is later than scheduled time (delayed) or if current flight has hop issues
  const isTimeDelayed = (scheduledTime: string | null | undefined, actualTime: string | null | undefined, hasCurrentFlightHopIssues?: boolean): boolean => {
    // If current flight has hop issues, show as delayed
    if (hasCurrentFlightHopIssues) {
      return true;
    }
    
    if (!scheduledTime || !actualTime) return false;
    
    try {
      // Convert time strings to comparable format
      // Assuming format like "10:30 AM" or "2:45 PM"
      const parseTime = (timeStr: string): number => {
        const [time, period] = timeStr.split(' ');
        const [hours, minutes] = time.split(':').map(Number);
        let hour24 = hours;
        
        if (period === 'PM' && hours !== 12) hour24 += 12;
        if (period === 'AM' && hours === 12) hour24 = 0;
        
        return hour24 * 60 + minutes; // Convert to minutes for comparison
      };
      
      const scheduledMinutes = parseTime(scheduledTime);
      const actualMinutes = parseTime(actualTime);
      
      return actualMinutes > scheduledMinutes;
    } catch (error) {
      return false;
    }
  };

  if (!info) return null;

  return (
    <>
      {/* --- Main Flight Info Popup --- */}
      <div
        ref={popupRef}
        className={
          info.type === 'airport' 
            ? 'locationContainer'
            : `hoverContainer ${info.isCurrentFlightIssue ? 'withIssue' : 'withoutIssue'}`
        }
        style={{
          position: "absolute",
          left: `${hoverPosition?.x || 0}px`,
          top: `${hoverPosition?.y || 0}px`,
          zIndex: 9999,
          pointerEvents: "auto",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          maxHeight: "248px",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div className={
          info.type === 'airport'
            ? 'locationHeader'
            : `hoverHeader ${info.isCurrentFlightIssue ? 'withIssue' : 'withoutIssue'}`
        }>
          <div className="headerText">
            <span>{info.type === 'airport' ? info.flightNumber?.toUpperCase() : info.flightNumber}</span>
            {/* Warning icon if future issue */}
            {info.isFutureFlightIssue && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  marginLeft: "8px",
                }}
                title="Future flight issue"
                dangerouslySetInnerHTML={{ __html: WarningIcon("#FFD700") }}
              />
            )}
             <span className="textView">({info.tripCount} Trips)</span>
          </div>
        </div>

        {/* Content */}
        <div className={info.type === 'airport' ? 'locationContent' : 'hoverContent'}>
          <div className="contentText">
            {info.type === 'airport' ? (
              <>
              </>
            ) : (
              <>
                <span>{info.dep}</span>
                <span className="hoverArrow">
                  <ArrowIcon />
                </span>
                <span>{info.arr}</span>
              </>
            )}
          </div>

          {/* Passengers */}
          <div className="passenger-parent scrollContent">
            {Array.isArray(info.passengers) &&
              info.passengers.map((p, idx) => (
                <div
                  key={`${p.tripId}-${p.pfhId}-${idx}`}
                  onClick={() => handlePassengerClick({...p, index: idx})}
                  className={`passenger-card cursor-pointer ${
                    (() => {
                      if (!selectedPassenger) return '';
                      
                      // Use index-based comparison since tripId + pfhId may not be unique
                      const isSelected = selectedPassenger.index !== undefined && selectedPassenger.index === idx;
                      
                      // Use index for unique identification since tripId + pfhId can have duplicates
                      return isSelected ? 'passenger-selected' : '';
                    })()
                  } ${info.passengers && info.passengers.length > 1 ? 'multiplePassenger' : ''}`}
                >
                  <div className="passengerBox">
                    <div className="passengerContainer">
                      <div className="passengerLabel">Passenger(s)</div>
                      <div className="passengerValue">                        
                        <span>{p.name}</span>
                        {/* Show warning icon if passenger has future flight issue */}
                        {p.futureFlightHasIssue && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              marginLeft: "8px",
                            }}
                            title="Future flight issue"
                            dangerouslySetInnerHTML={{ __html: WarningIcon("#FFD700") }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="passengerContainer">
                      <div className="passengerLabel">Group</div>
                      <div className="passengerValue">{p.group}</div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* --- Passenger Detail Popup --- */}
      {selectedPassenger && (
        <div
          ref={detailRef}
          className={
            info.type === 'airport'
              ? 'locationContainer multiplePassenger passengerClick'
              : `detailContainer ${selectedPassenger.currentFlightHasIssue ? 'withIssue' : 'withoutIssue'} multiplePassenger`
          }
          style={{
            position: "absolute",
            // left: `${(hoverPosition?.x || 0) + 225}px`, // 220px + 5px from multiplePassenger CSS class
            left: isPopupOnLeft 
              ? `${(hoverPosition?.x || 0) - 405 - 5}px` // Position on left: subtract detail popup width (405px) and spacing (5px)
              : `${(hoverPosition?.x || 0) + 220 + 5}px`, // Position on right: hover popup width (220px) + spacing (5px)
            
            top: `${(hoverPosition?.y || 0) - 170}px`, // Adjusted offset to center passenger popup with hover popup
            zIndex: 9999,
            height: "506px", // Fixed height for both airport and flight popups
            maxHeight: "506px",
            overflowY: "auto",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segue UI', sans-serif",
            width: "405px", // Same width for both airport and flight popups
          }}
        >
          {/* Header */}
          <div className={
            info.type === 'airport'
              ? 'locationHeader passengerClick'
              : `detailHeader ${selectedPassenger.currentFlightHasIssue ? 'withIssue' : 'withoutIssue'}`
          }>
            <div className="headerText">
              <span>
                {/* 
                  RECORD LOCATOR DISPLAY LOGIC:
                  - Show loading text until a passenger is selected and their trip data loads
                  - Once loaded, show the specific passenger's record locator
                  - This ensures we always show the correct record locator for the selected passenger
                */}
                {(selectedPassenger && transformedTrip?.recordLocator) 
                  ? transformedTrip.recordLocator.toUpperCase()
                  : (selectedPassenger && loading) 
                    ? 'Loading...'
                    : 'Select Passenger'
                }
              </span>
              {/* Warning icon if selected passenger has future flight issue */}
              {selectedPassenger?.futureFlightHasIssue && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginLeft: "8px",
                  }}
                  title="Future flight issue"
                  dangerouslySetInnerHTML={{ __html: WarningIcon("#FFD700") }}
                />
              )}
              {/* <span className="textView">({info.tripCount} Trips)</span> */}
            </div>
          </div>

          {/* Detail Content */}
          <div 
            className={info.type === 'airport' ? 'locationContent scrollContent' : 'detailContent scrollContent'}
            style={{
              maxHeight: '450px', // Same content height for both airport and flight
              height: '450px',
              overflowY: 'auto'
            }}
          >
            <div className="content1">
              {loading && (
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  padding: "60px 20px"
                }}>
                  {/* Loading Spinner */}
                  <div style={{
                    width: "48px",
                    height: "48px",
                    border: "4px solid #2A3A4F",
                    borderTop: info.type === 'airport' 
                      ? "4px solid #465569ff" // Ash color for airport
                      : selectedPassenger?.currentFlightHasIssue 
                        ? "4px solid #FB4B4B" // Red for flight with issues
                        : "4px solid #09AF91", // Green for flight without issues
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }}></div>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              )}

              {!loading && !transformedTrip && (
                <div style={{ opacity: 0.8, padding: "20px 0", textAlign: "center" }}>
                  No trip details available
                </div>
              )}

              {!loading && transformedTrip && (
                <>
                  {/* Trip and Passenger Details Header */}
                  <div style={{ display: "flex" }}>
                    <div style={{
                      width: "25%",
                      borderRight: "1.5px solid #2A3A4F",
                      padding: "5px 0px",
                    }}>
                      <div className="passengerLabel">Trip</div>
                      {/* Check Trip Assist feature flag to determine if record locator should be a hyperlink */}
                      {hasTripAssistAccess() ? (
                        <div 
                          style={{
                            fontWeight: 500,
                            fontSize: "14px",
                            lineHeight: "18px",
                            display: "flex",
                            alignItems: "center",
                            textDecorationLine: "underline",
                            color: "#386DFF",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            const tripId = transformedTrip?.id || selectedPassenger?.tripId;
                            if (tripId) {
                              // Follow the same pattern as existing app - use relative URL for same domain navigation
                              const tripUrl = `/trip/${tripId}`;
                              window.open(tripUrl, '_blank');
                            }
                          }}
                          title={`Click to view trip details: ${transformedTrip.recordLocator || transformedTrip.id}`}
                        >
                          {((transformedTrip.recordLocator || transformedTrip.id) || '').length > 6 
                            ? `${(transformedTrip.recordLocator || transformedTrip.id).substring(0, 6)}...`
                            : (transformedTrip.recordLocator || transformedTrip.id)
                          }
                        </div>
                      ) : (
                        <div 
                          style={{
                            fontWeight: 500,
                            fontSize: "14px",
                            lineHeight: "18px",
                            display: "flex",
                            alignItems: "center",
                            color: "white", // Normal text color without hyperlink styling
                          }}
                        >
                          {((transformedTrip.recordLocator || transformedTrip.id) || '').length > 6 
                            ? `${(transformedTrip.recordLocator || transformedTrip.id).substring(0, 6)}...`
                            : (transformedTrip.recordLocator || transformedTrip.id)
                          }
                        </div>
                      )}
                    </div>
                    <div style={{
                      width: "75%",
                      display: "flex",
                      justifyContent: "flex-end",
                      textAlign: "right",
                    }}>
                      <div className="passengerBox" style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                      }}>
                        <div className="passengerContainer">
                          <div className="passengerLabel">Passenger(s)</div>
                          <div className="passengerValue">
                            {transformedTrip.passengerName || selectedPassenger.name}
                          </div>
                        </div>
                        <div className="passengerContainer">
                          <div className="passengerLabel">Group</div>
                          <div className="passengerValue">
                            {selectedPassenger.group || transformedTrip.groupName}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div
                    className="contentText"
                    style={{
                      padding: "10px 0px",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      gap: "4px",
                    }}
                  >
                    <span style={{ fontWeight: 400 }}>Leg</span>{" "}
                    <span style={{ fontWeight: 600 }}>
                      {transformedTrip.currentLegIndex != null && transformedTrip.currentLegIndex >= 0
                        ? transformedTrip.currentLegIndex + 1
                        : "-"}
                    </span>{" "}
                    <span style={{ fontWeight: 400 }}>of</span>{" "}
                    <span style={{ fontWeight: 600 }}>
                      {transformedTrip.legCount != null && transformedTrip.legCount > 0
                        ? transformedTrip.legCount
                        : "-"}
                    </span>{" "}
                    <span style={{ fontWeight: 400 }}>-</span>{" "}
                    <span style={{ fontWeight: 600 }}>
                      {transformedTrip.currentLegDepartureCityState || "-"}
                    </span>{" "}
                    <span style={{ fontWeight: 400 }}>to</span>{" "}
                    <span style={{ fontWeight: 600 }}>
                      {transformedTrip.currentLegArrivalCityState || "-"}
                    </span>
                  </div>


                  {/* Dashed Line */}
                  <div style={{
                    height: "1.5px",
                    background: "repeating-linear-gradient(to right, #2A3A4F, #2A3A4F 10px, transparent 4px, transparent 20px)",
                  }}></div>

                  {/* Current Flight Details - Location & Time */}
                  {transformedTrip.currentFlight && (
                    <>
                      {/* Location Box */}
                      <div style={{ display: "flex" }}>
                        <div style={{
                          borderBottom: "1.5px solid #2A3A4F",
                          padding: "16px 0px",
                          width: "45%",
                          display: "flex",
                        }}>
                          <div style={{ width: "85%" }}>
                            <div style={{
                              fontWeight: 500,
                              fontSize: "20px",
                              lineHeight: "24px",
                              textAlign: "center",
                              color: "#FDFDFD",
                            }}>{transformedTrip.currentFlight.departureAirportCode || "-"}</div>
                            <div className="passengerLabel" style={{
                              textAlign: "center",
                            }}>{transformedTrip.currentFlight.departureCityState || "-"}</div>
                          </div>
                          <div style={{
                            width: "15%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}>
                            <div style={{
                              border: "1px solid #2A3A4F",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}></div>
                          </div>
                        </div>
                        <div style={{
                          width: "10%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}>
                          ✈
                        </div>
                        <div style={{
                          borderBottom: "1.5px solid #2A3A4F",
                          padding: "16px 0px",
                          width: "45%",
                          display: "flex",
                        }}>
                          <div style={{
                            width: "15%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}>
                            <div style={{
                              border: "1px solid #2A3A4F",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}></div>
                          </div>
                          <div style={{ width: "85%" }}>
                            <div style={{
                              fontWeight: 500,
                              fontSize: "20px",
                              lineHeight: "24px",
                              textAlign: "center",
                              color: "#FDFDFD",
                            }}>{transformedTrip.currentFlight.arrivalAirportCode || "-"}</div>
                            <div className="passengerLabel" style={{
                              textAlign: "center",
                            }}>{transformedTrip.currentFlight.arrivalCityState || "-"}</div>
                          </div>
                        </div>
                      </div>

                      {/* Time Container */}
                      <div style={{ display: "flex" }}>
                        <div style={{ width: "45%", display: "flex" }}>
                          <div style={{
                            paddingTop: "10px",
                            width: "85%",
                            borderRight: "1.5px solid #2A3A4F",
                            marginRight: "8px",
                          }}>
                            <div style={{
                              fontWeight: 500,
                              fontSize: "10px",
                              lineHeight: "14px",
                              color: "#8998AB",
                            }}>Scheduled</div>
                            <div style={{ display: "flex" }}>
                              {(() => {
                                const timeStr = transformedTrip.currentFlight.scheduledDepartureTime || "-";
                                if (timeStr === "-") {
                                  return <span style={{
                                    fontWeight: 500,
                                    fontSize: "15px",
                                    lineHeight: "22px",
                                    color: "#FDFDFD",
                                  }}>-</span>;
                                }
                                const parts = timeStr.split(' ');
                                const time = parts[0];
                                const period = parts[1];
                                return (
                                  <>
                                    <span style={{
                                      fontWeight: 500,
                                      fontSize: "15px",
                                      lineHeight: "22px",
                                      color: "#FDFDFD",
                                    }}>{time}</span>
                                    {period && (
                                      <span style={{
                                        fontWeight: 500,
                                        fontSize: "11px",
                                        lineHeight: "22px",
                                        color: "#FDFDFD",
                                        marginLeft: "2px",
                                      }}>{period}</span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                          <div style={{ paddingTop: "10px", width: "85%" }}>
                            <div style={{
                              fontWeight: 500,
                              fontSize: "10px",
                              lineHeight: "14px",
                              color: "#8998AB",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}>
                              Actual
                              {isTimeDelayed(
                                transformedTrip.currentFlight.scheduledDepartureTime,
                                transformedTrip.currentFlight.actualDepartureTime,
                                hasCurrentFlightHopIssues(transformedTrip)
                              ) && (
                                <div style={{
                                  width: 0,
                                  height: 0,
                                  borderLeft: "4px solid transparent",
                                  borderRight: "4px solid transparent",
                                  borderBottom: "6px solid #FF4C4C",
                                  display: "inline-block",
                                }} />
                              )}
                            </div>
                            <div style={{ display: "flex" }}>
                              {(() => {
                                const timeStr = transformedTrip.currentFlight.actualDepartureTime || "-";
                                const isDelayed = isTimeDelayed(
                                  transformedTrip.currentFlight.scheduledDepartureTime,
                                  transformedTrip.currentFlight.actualDepartureTime,
                                  hasCurrentFlightHopIssues(transformedTrip)
                                );
                                const timeColor = isDelayed ? "#FF4C4C" : "#FDFDFD";
                                
                                if (timeStr === "-") {
                                  return <span style={{
                                    fontWeight: 500,
                                    fontSize: "15px",
                                    lineHeight: "22px",
                                    color: timeColor,
                                  }}>-</span>;
                                }
                                const parts = timeStr.split(' ');
                                const time = parts[0];
                                const period = parts[1];
                                return (
                                  <>
                                    <span style={{
                                      fontWeight: 500,
                                      fontSize: "15px",
                                      lineHeight: "22px",
                                      color: timeColor,
                                    }}>{time}</span>
                                    {period && (
                                      <span style={{
                                        fontWeight: 500,
                                        fontSize: "11px",
                                        lineHeight: "22px",
                                        color: timeColor,
                                        marginLeft: "2px",
                                      }}>{period}</span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        <div style={{ width: "10%" }}></div>
                        <div style={{ width: "45%", display: "flex" }}>
                          <div style={{
                            paddingTop: "10px",
                            width: "85%",
                            borderRight: "1.5px solid #2A3A4F",
                            marginRight: "8px",
                          }}>
                            <div style={{
                              fontWeight: 500,
                              fontSize: "10px",
                              lineHeight: "14px",
                              color: "#8998AB",
                            }}>Scheduled</div>
                            <div style={{ display: "flex" }}>
                              {(() => {
                                const timeStr = transformedTrip.currentFlight.scheduledArrivalTime || "-";
                                if (timeStr === "-") {
                                  return <span style={{
                                    fontWeight: 500,
                                    fontSize: "15px",
                                    lineHeight: "22px",
                                    color: "#FDFDFD",
                                  }}>-</span>;
                                }
                                const parts = timeStr.split(' ');
                                const time = parts[0];
                                const period = parts[1];
                                return (
                                  <>
                                    <span style={{
                                      fontWeight: 500,
                                      fontSize: "15px",
                                      lineHeight: "22px",
                                      color: "#FDFDFD",
                                    }}>{time}</span>
                                    {period && (
                                      <span style={{
                                        fontWeight: 500,
                                        fontSize: "11px",
                                        lineHeight: "22px",
                                        color: "#FDFDFD",
                                        marginLeft: "2px",
                                      }}>{period}</span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                          <div style={{ paddingTop: "10px", width: "85%" }}>
                            <div style={{
                              fontWeight: 500,
                              fontSize: "10px",
                              lineHeight: "14px",
                              color: "#8998AB",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}>
                              Actual
                              {isTimeDelayed(
                                transformedTrip.currentFlight.scheduledArrivalTime,
                                transformedTrip.currentFlight.actualArrivalTime,
                                hasCurrentFlightHopIssues(transformedTrip)
                              ) && (
                                <div style={{
                                  width: 0,
                                  height: 0,
                                  borderLeft: "4px solid transparent",
                                  borderRight: "4px solid transparent",
                                  borderBottom: "6px solid #FF4C4C",
                                  display: "inline-block",
                                }} />
                              )}
                            </div>
                            <div style={{ display: "flex" }}>
                              {(() => {
                                const timeStr = transformedTrip.currentFlight.actualArrivalTime || "-";
                                const isDelayed = isTimeDelayed(
                                  transformedTrip.currentFlight.scheduledArrivalTime,
                                  transformedTrip.currentFlight.actualArrivalTime,
                                  hasCurrentFlightHopIssues(transformedTrip)
                                );
                                const timeColor = isDelayed ? "#FF4C4C" : "#FDFDFD";
                                
                                if (timeStr === "-") {
                                  return <span style={{
                                    fontWeight: 500,
                                    fontSize: "15px",
                                    lineHeight: "22px",
                                    color: timeColor,
                                  }}>-</span>;
                                }
                                const parts = timeStr.split(' ');
                                const time = parts[0];
                                const period = parts[1];
                                return (
                                  <>
                                    <span style={{
                                      fontWeight: 500,
                                      fontSize: "15px",
                                      lineHeight: "22px",
                                      color: timeColor,
                                    }}>{time}</span>
                                    {period && (
                                      <span style={{
                                        fontWeight: 500,
                                        fontSize: "11px",
                                        lineHeight: "22px",
                                        color: timeColor,
                                        marginLeft: "2px",
                                      }}>{period}</span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Flight Card Section */}
                  {transformedTrip.flights && transformedTrip.flights.length > 0 && (
                    <div style={{
                      background: "#121B26",
                      borderRadius: "32px 32px 0px 0px",
                      padding: "10px",
                      marginTop: "10px",
                      marginRight: "6px",
                      position: "relative", // Enable positioned connection lines
                    }}>
                      <ConnectionLines />
                      {transformedTrip.flights.map((flight, flightIndex) => {
                        // Check if flight has hop issues and override status accordingly
                        const hasIssues = flight.hasHopIssues || flight.hasConnectionIssues;
                        const statusStyle = hasIssues ? {
                          // Red styling for flights with issues
                          color: "#FFFFFF", // White text for better contrast
                          border: "1px solid #FF4C4C",
                          backgroundColor: "#FF4C4C", // Red background
                        } : {
                          // Green styling for normal flights
                          color: "#55FC7F", // Green text
                          border: "1px solid #84FFA3",
                          backgroundColor: "transparent",
                        };
                        const hasConnection = flightIndex < transformedTrip.flights.length - 1 && flight.connection;

                        return (
                          <div 
                            key={flightIndex}
                            ref={(el) => {
                              if (flightCardsRef.current) {
                                flightCardsRef.current[flightIndex] = el;
                              }
                            }}
                          >
                            <div style={{ padding: "10px" }}>
                              <div style={{ display: "flex" }}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <div style={{
                                    background: "white", // White fill for all circles
                                    border: 
                                      // Temporarily disable circle highlighting for airport passengers
                                      (info.type === 'airport' && selectedPassenger)
                                        ? "3px solid #9CA3AF" // Gray for all when from airport
                                        : flight.isCurrentFlight 
                                          ? "3px solid #386DFF" // Blue border for current flight
                                          : "3px solid #9CA3AF", // Gray border for future flights
                                    width: "13px",
                                    height: "13px",
                                    borderRadius: "50%",
                                  }}></div>
                                </div>
                                <div style={{
                                  border: 
                                    // Temporarily disable hop highlighting for airport passengers to see connection highlighting
                                    (info.type === 'airport' && selectedPassenger) 
                                      ? "1.5px solid #4B5F7A" // Gray for all flights when from airport
                                      : flight.isCurrentFlight 
                                        ? "1.5px solid #386DFF" // Blue for current flight
                                        : flight.hasHopIssues 
                                          ? "1.5px solid #FF4C4C" // Red for future flight with hop issues
                                          : "1.5px solid #4B5F7A", // Gray for normal future flight
                                  borderRadius: "6px",
                                  width: "337px",
                                  margin: "0px 8px",
                                }}>
                                  <div style={{
                                    background: "#1C2633",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    height: "30px",
                                  }}>
                                    <div style={{
                                      width: "50%",
                                      textAlign: "left",
                                      color: "#FDFDFD",
                                      padding: "0px 8px",
                                    }}>
                                      <span style={{
                                        fontWeight: 700,
                                        fontSize: "12px",
                                        lineHeight: "16px",
                                      }}>{flight.flightNumber || ""}</span>
                                      <span style={{
                                        fontWeight: 350,
                                        fontSize: "9px",
                                        lineHeight: "14px",
                                        textAlign: "right",
                                        paddingLeft: "8px",
                                      }}>{flight.departureDate || ""}</span>
                                    </div>
                                    <div style={{
                                      margin: "2px 8px",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      width: "50%",
                                      height: "20px",
                                      borderRadius: "12px",
                                      fontWeight: 700,
                                      fontSize: "10px",
                                      lineHeight: "12px",
                                      textTransform: "uppercase",
                                      ...statusStyle,
                                    }}>
                                      {formatStatusMessage(flight)}
                                    </div>
                                  </div>
                                  <div style={{ display: "flex", padding: "8px 0px" }}>
                                    <div style={{ 
                                      width: "33%",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-end", // ✅ aligns content to the right
                                      textAlign: "right", }}>
                                      <div style={{
                                        fontWeight: 500,
                                        fontSize: "12px",
                                        lineHeight: "16px",
                                        color: "#A9BBC7",
                                      }}>{flight.departureAirportCode || ""}</div>
                                      <div style={{
                                        fontWeight: 700,
                                        fontSize: "12px",
                                        lineHeight: "16px",
                                        color: "#FDFDFD",
                                      }}>{flight.departureTime || "-"}</div>
                                    </div>
                                    <div style={{
                                      width: "33%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}>
                                      <div style={{
                                        width: "26px",
                                        height: "1px",
                                        border: "1px solid #4B5F7A",
                                        margin: "0px 6px",
                                      }}></div>
                                      <span>✈</span>
                                      <div style={{
                                        width: "26px",
                                        height: "1px",
                                        border: "1px solid #4B5F7A",
                                        margin: "0px 6px",
                                      }}></div>
                                    </div>
                                    <div style={{ width: "33%" }}>
                                      <div style={{
                                        fontWeight: 500,
                                        fontSize: "12px",
                                        lineHeight: "16px",
                                        color: "#A9BBC7",
                                      }}>{flight.arrivalAirportCode || ""}</div>
                                      <div style={{
                                        fontWeight: 700,
                                        fontSize: "12px",
                                        lineHeight: "16px",
                                        color: "#FDFDFD",
                                      }}>{flight.arrivalTime || "-"}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {hasConnection && (() => {
                              // Check if this connection should be highlighted in blue
                              // This happens when:
                              // 1. The popup is from airport hover (info.type === 'airport')
                              // 2. A passenger is selected
                              // 3. The current flight's flightHistoryId matches the passenger's pfhId (completed flight)
                              // 4. There is a next flight (the connection represents waiting time for next flight)
                              const nextFlight = transformedTrip.flights[flightIndex + 1];
                              const isPassengerWaitingForConnection = 
                                info.type === 'airport' &&
                                selectedPassenger && 
                                selectedPassenger.pfhId && 
                                flight.flightHistoryId && 
                                nextFlight && // Must have a next flight to wait for
                                String(selectedPassenger.pfhId) === String(flight.flightHistoryId);



                              return (
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                  <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    padding: "8px 24px",
                                    width: "235px",
                                    height: "28px",
                                    borderRadius: "4px",
                                    fontWeight: 500,
                                    fontSize: "12px",
                                    lineHeight: "16px",
                                    textAlign: "center",
                                    background: flight.hasConnectionIssues 
                                      ? "#FFB3B3" 
                                      : "#1C2633", // Keep dark background for all normal connections
                                    border: isPassengerWaitingForConnection 
                                      ? "2px solid #4A90E2" // Blue border when passenger is waiting at airport for this connection
                                      : "none", // No border for normal connections
                                    color: flight.hasConnectionIssues 
                                      ? "#FFFFFF" 
                                      : "#A9BBC7", // Keep original text colors
                                  }}>
                                    {formatConnectionTime(flight.connection) || "CONNECTION"}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FlightPopup;